import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Archive,
  Customer,
  DeviceType,
  Extinguisher,
  Hose,
  HoseConfig,
  Manufacturer,
  Model,
  ServicePrice,
  Settings,
  SparePart,
  Visit,
  Worker,
} from '@/data/types';
import { Lang } from '@/lib/lang';
import { getDecoded, onChange as onAuthChange } from '@/lib/auth';
import {
  put as adapterPut,
  removeByTypeId as adapterRemove,
} from '@/lib/store-adapter';
import {
  runShadow,
  pendingCount,
  subscribe as subscribeSyncQueue,
  setSyncErrorHandler,
  startSyncQueue,
} from '@/lib/sync-queue';
import { setReplicationErrorHandler, setOnlineHandler } from '@/lib/couch';
import { canWrite } from '@/lib/permissions';
import { t } from '@/lib/i18n';

// All domain mutators now shadow-write to PouchDB via the store-adapter so the
// per-tenant CouchDB receives the change (see W1 #8). The zustand persist blob
// still backs the UI for offline reads until the read-path is migrated.

interface CurrentUser {
  name: string;
  email: string;
  tenantId: string;
  roles: string[];
}

function deriveUserFromJwt(): CurrentUser | null {
  const d = getDecoded();
  if (!d) return null;
  return {
    name: d.name ?? d.email ?? d.sub,
    email: d.email ?? d.sub ?? '',
    tenantId: d.tenant_id ?? '',
    roles: d.roles ?? [],
  };
}

// Block a write the current role is not allowed to persist, mirroring the
// server's validate_doc_update. PouchDB's local validator runs with empty roles
// so it won't enforce this offline — without the client guard a forbidden write
// lands in the local DB and only bounces on push (silent divergence). Returns
// false and toasts when blocked. Admin-only catalog pages are already
// route-gated, so this is defense-in-depth at the persistence boundary.
function guardWrite(type: string): boolean {
  const roles = deriveUserFromJwt()?.roles ?? [];
  if (canWrite(roles, type)) return true;
  const { lang, toast } = useApp.getState();
  toast(t('perm.denied', lang));
  console.warn('[perm] blocked write to type', type, 'for roles', roles);
  return false;
}

// Write-through to PouchDB via the durable sync queue: on failure the write is
// retried automatically and the first failure is surfaced to the user, so a
// transient error never silently drops a record.
function shadowPutCustomer(c: Customer): void {
  if (!guardWrite('customer')) return;
  const doc = {
    ...(c as unknown as Record<string, unknown>),
    _id: `customer:${c.id}`,
    type: 'customer',
  };
  runShadow(`put:customer:${c.id}`, () => adapterPut(doc as never));
}

/**
 * Generic shadow-put for non-customer domain entities. The caller supplies the
 * doc `type` and the id segment used in `_id`.
 */
function shadowPut(type: string, idPart: string, doc: unknown): void {
  if (!guardWrite(type)) return;
  const next = {
    ...(doc as Record<string, unknown>),
    _id: `${type}:${idPart}`,
    type,
  };
  runShadow(`put:${type}:${idPart}`, () => adapterPut(next as never));
}

/**
 * Write-through delete to PouchDB so the CouchDB delete replicates. Queued and
 * retried on failure like the puts.
 */
function shadowRemove(type: string, idPart: string): void {
  if (!guardWrite(type)) return;
  runShadow(`remove:${type}:${idPart}`, () => adapterRemove(type, idPart));
}

export enum FilterStatus {
  ALL = 'all',
  DONE = 'done',
  WAITING = 'waiting',
  TODO = 'todo',
  NONE = 'none',
}

export enum SortDir {
  ASC = 'asc',
  DESC = 'desc',
}

interface AppState {
  online: boolean;
  unsyncedCount: number;
  customers: Customer[];
  selectedCustomerIds: Set<string>;
  manufacturers: Manufacturer[];
  deviceTypes: DeviceType[];
  spareParts: SparePart[];
  servicePrices: ServicePrice[];
  visits: Visit[];
  workers: Worker[];
  settings: Settings;
  hoseConfig: HoseConfig;
  toasts: { id: number; msg: string }[];
  search: string;
  filterStatus: FilterStatus;
  filterWorker: string;
  filterCity: string;
  sortCol: string;
  sortDir: SortDir;
  tourSeen: boolean;
  tourActive: boolean;
  tourStep: number;
  updatesSeen: boolean;
  visitedUpdates: string[];
  focusSelector: string | null;
  focusLabel: string | null;
  lang: Lang;
  sidebarOpen: boolean;
  hydrated: boolean;
  currentUser: CurrentUser | null;
  activeTenantId: string | null;
  setHydrated: (v: boolean) => void;
  setCurrentUser: (u: CurrentUser | null) => void;
  setActiveTenantId: (id: string | null) => void;
  setLang: (l: Lang) => void;
  setOnline: (v: boolean) => void;
  setSidebarOpen: (v: boolean) => void;
  toggleSidebar: () => void;
  toggleSelected: (id: string) => void;
  selectAll: (v: boolean) => void;
  clearFilters: () => void;
  setFilter: (
    k: 'search' | 'filterStatus' | 'filterWorker' | 'filterCity',
    v: string
  ) => void;
  setSort: (col: string) => void;
  toast: (msg: string) => void;
  dismissToast: (id: number) => void;
  updateCustomer: (id: string, patch: Partial<Customer>) => void;
  addCustomer: (c: Customer) => void;
  deleteCustomer: (id: string) => void;
  upsertExtinguisher: (customerId: string, ext: Extinguisher) => void;
  deleteExtinguisher: (customerId: string, extId: string) => void;
  batchUpdateInspection: (customerId: string) => void;
  upsertHose: (customerId: string, hose: Hose) => void;
  deleteHose: (customerId: string, hoseId: string) => void;
  createArchive: (customerId: string) => void;
  updateSettings: (patch: Partial<Settings>) => void;
  updateHoseConfig: (patch: Partial<HoseConfig>) => void;
  upsertManufacturer: (m: Manufacturer) => void;
  deleteManufacturer: (id: string) => void;
  upsertModel: (mfrId: string, model: Model) => void;
  deleteModel: (mfrId: string, modelId: string) => void;
  upsertDeviceType: (t: DeviceType) => void;
  deleteDeviceType: (id: string) => void;
  upsertSparePart: (p: SparePart) => void;
  deleteSparePart: (articleNr: string) => void;
  upsertServicePrice: (p: ServicePrice) => void;
  deleteServicePrice: (articleNr: string) => void;
  upsertVisit: (v: Visit) => void;
  deleteVisit: (id: string) => void;
  upsertWorker: (w: Worker) => void;
  deleteWorker: (id: string) => void;
  startTour: () => void;
  nextTourStep: () => void;
  prevTourStep: () => void;
  endTour: () => void;
  setTourStep: (n: number) => void;
  markUpdatesSeen: () => void;
  markUpdateItemVisited: (key: string) => void;
  setFocus: (selector: string | null, label?: string | null) => void;
  replaceAll: (data: Partial<AppState>) => void;
}

let toastId = 0;

const computeDeviceCounts = (c: Customer): Record<string, number> => {
  const counts: Record<string, number> = {};
  c.extinguishers.forEach(e => {
    if (!e.type) return;
    counts[e.type] = (counts[e.type] ?? 0) + 1;
  });
  if (c.hoses.length) counts.loeschposten = c.hoses.length;
  return counts;
};

// Backend-only data: initial state is empty. PouchDB + tenant seedCatalog
// (apps/api/internal/handlers/tenants.go) is the sole source of truth.
// initStoreFromPouchDB (lib/init-store.ts) hydrates this after auth.
const EMPTY_SETTINGS: Settings = {
  country: '',
  companyName: '',
  vatNumber: '',
  street: '',
  zip: '',
  city: '',
  phone: '',
  email: '',
  vatPercent: 0,
  currency: '',
  rounding: '',
  paymentTerms: 0,
  inspectionInterval: 0,
  boostAgeLimit: 0,
  permanentAgeLimit: 0,
  hoseTestInterval: 0,
  travelSurcharge: 0,
  iban: '',
};
const EMPTY_HOSE_CONFIG: HoseConfig = {
  hoseDiameters: [],
  connectionDiameters: [],
  hoseModels: [],
};

export const useApp = create<AppState>()(
  persist(
    (set, get) => ({
      online: true,
      unsyncedCount: 0,
      customers: [],
      selectedCustomerIds: new Set<string>(),
      manufacturers: [],
      deviceTypes: [],
      spareParts: [],
      servicePrices: [],
      visits: [],
      workers: [],
      settings: EMPTY_SETTINGS,
      hoseConfig: EMPTY_HOSE_CONFIG,
      toasts: [],
      search: '',
      filterStatus: FilterStatus.ALL,
      filterWorker: '',
      filterCity: '',
      sortCol: 'name',
      sortDir: SortDir.ASC,
      tourSeen: false,
      tourActive: false,
      tourStep: 0,
      updatesSeen: false,
      visitedUpdates: [],
      focusSelector: null,
      focusLabel: null,
      lang: Lang.EN,
      sidebarOpen: false,
      hydrated: false,
      currentUser: deriveUserFromJwt(),
      activeTenantId: deriveUserFromJwt()?.tenantId ?? null,
      setHydrated: v => set({ hydrated: v }),
      setCurrentUser: u =>
        set({ currentUser: u, activeTenantId: u?.tenantId ?? null }),
      setActiveTenantId: id => set({ activeTenantId: id }),
      setLang: l => set({ lang: l }),
      setOnline: v => set({ online: v }),
      setSidebarOpen: v => set({ sidebarOpen: v }),
      toggleSidebar: () => set({ sidebarOpen: !get().sidebarOpen }),
      toggleSelected: id => {
        const next = new Set(get().selectedCustomerIds);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        set({ selectedCustomerIds: next });
      },
      selectAll: v => {
        if (v)
          set({ selectedCustomerIds: new Set(get().customers.map(c => c.id)) });
        else set({ selectedCustomerIds: new Set() });
      },
      clearFilters: () =>
        set({
          search: '',
          filterStatus: FilterStatus.ALL,
          filterWorker: '',
          filterCity: '',
        }),
      setFilter: (k, v) => set({ [k]: v } as Partial<AppState>),
      setSort: col => {
        const cur = get();
        if (cur.sortCol === col)
          set({
            sortDir: cur.sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC,
          });
        else set({ sortCol: col, sortDir: SortDir.ASC });
      },
      toast: msg => {
        const id = ++toastId;
        set({ toasts: [...get().toasts, { id, msg }] });
        setTimeout(() => get().dismissToast(id), 3000);
      },
      dismissToast: id =>
        set({ toasts: get().toasts.filter(t => t.id !== id) }),
      updateCustomer: (id, patch) => {
        const next = get().customers.map(c =>
          c.id === id ? { ...c, ...patch } : c
        );
        set({ customers: next });
        const updated = next.find(c => c.id === id);
        if (updated) shadowPutCustomer(updated);
      },
      addCustomer: c => {
        set({ customers: [...get().customers, c] });
        shadowPutCustomer(c);
      },
      deleteCustomer: id => {
        set({ customers: get().customers.filter(c => c.id !== id) });
        shadowRemove('customer', id);
      },
      upsertExtinguisher: (customerId, ext) => {
        const next = get().customers.map(c => {
          if (c.id !== customerId) return c;
          const exists = c.extinguishers.some(e => e.id === ext.id);
          const extinguishers = exists
            ? c.extinguishers.map(e => (e.id === ext.id ? ext : e))
            : [...c.extinguishers, ext];
          const nc = { ...c, extinguishers };
          return { ...nc, deviceCounts: computeDeviceCounts(nc) };
        });
        set({ customers: next });
        const updated = next.find(c => c.id === customerId);
        if (updated) shadowPutCustomer(updated);
      },
      deleteExtinguisher: (customerId, extId) => {
        const next = get().customers.map(c => {
          if (c.id !== customerId) return c;
          const nc = {
            ...c,
            extinguishers: c.extinguishers.filter(e => e.id !== extId),
          };
          return { ...nc, deviceCounts: computeDeviceCounts(nc) };
        });
        set({ customers: next });
        const updated = next.find(c => c.id === customerId);
        if (updated) shadowPutCustomer(updated);
      },
      batchUpdateInspection: customerId => {
        const { settings } = get();
        const today = new Date().toISOString().slice(0, 10);
        const nextDate = new Date();
        nextDate.setFullYear(
          nextDate.getFullYear() + settings.inspectionInterval
        );
        const nextStr = nextDate.toISOString().slice(0, 10);
        const next = get().customers.map(c => {
          if (c.id !== customerId) return c;
          return {
            ...c,
            lastInspection: today,
            nextInspection: nextStr,
            extinguishers: c.extinguishers.map(e => ({
              ...e,
              lastInspection: today,
              nextInspection: nextStr,
              maintainedThisCycle: true,
            })),
          };
        });
        set({ customers: next });
        const updated = next.find(c => c.id === customerId);
        if (updated) shadowPutCustomer(updated);
      },
      upsertHose: (customerId, hose) => {
        const next = get().customers.map(c => {
          if (c.id !== customerId) return c;
          const exists = c.hoses.some(h => h.id === hose.id);
          const hoses = exists
            ? c.hoses.map(h => (h.id === hose.id ? hose : h))
            : [...c.hoses, hose];
          const nc = { ...c, hoses };
          return { ...nc, deviceCounts: computeDeviceCounts(nc) };
        });
        set({ customers: next });
        const updated = next.find(c => c.id === customerId);
        if (updated) shadowPutCustomer(updated);
      },
      deleteHose: (customerId, hoseId) => {
        const next = get().customers.map(c => {
          if (c.id !== customerId) return c;
          const nc = {
            ...c,
            hoses: c.hoses.filter(h => h.id !== hoseId),
          };
          return { ...nc, deviceCounts: computeDeviceCounts(nc) };
        });
        set({ customers: next });
        const updated = next.find(c => c.id === customerId);
        if (updated) shadowPutCustomer(updated);
      },
      createArchive: customerId => {
        const next = get().customers.map(c => {
          if (c.id !== customerId) return c;
          const archive: Archive = {
            id: `A-${Date.now()}`,
            date: new Date().toISOString().slice(0, 10),
            deviceCount: c.extinguishers.length + c.hoses.length,
            technician: c.worker ?? 'Unknown',
          };
          return { ...c, archives: [archive, ...c.archives] };
        });
        set({ customers: next });
        const updated = next.find(c => c.id === customerId);
        if (updated) shadowPutCustomer(updated);
      },
      updateSettings: patch => {
        const next = { ...get().settings, ...patch };
        set({ settings: next });
        // settings is a singleton — stable id keeps replication idempotent
        shadowPut('settings', 'settings', next);
      },
      updateHoseConfig: patch => {
        const next = { ...get().hoseConfig, ...patch };
        set({ hoseConfig: next });
        // hoseConfig is a singleton — stable id keeps replication idempotent
        shadowPut('hose_config', 'hose_config', next);
      },
      upsertManufacturer: m => {
        const cur = get().manufacturers;
        const next = cur.some(x => x.id === m.id)
          ? cur.map(x => (x.id === m.id ? m : x))
          : [...cur, m];
        set({ manufacturers: next });
        shadowPut('manufacturer', m.id, m);
      },
      deleteManufacturer: id => {
        set({ manufacturers: get().manufacturers.filter(m => m.id !== id) });
        shadowRemove('manufacturer', id);
      },
      upsertModel: (mfrId, model) => {
        const next = get().manufacturers.map(m => {
          if (m.id !== mfrId) return m;
          const exists = m.models.some(x => x.id === model.id);
          return {
            ...m,
            models: exists
              ? m.models.map(x => (x.id === model.id ? model : x))
              : [...m.models, model],
          };
        });
        set({ manufacturers: next });
        // Models live nested on the manufacturer doc — replicate the parent
        const updated = next.find(m => m.id === mfrId);
        if (updated) shadowPut('manufacturer', updated.id, updated);
      },
      deleteModel: (mfrId, modelId) => {
        const next = get().manufacturers.map(m =>
          m.id === mfrId
            ? { ...m, models: m.models.filter(x => x.id !== modelId) }
            : m
        );
        set({ manufacturers: next });
        const updated = next.find(m => m.id === mfrId);
        if (updated) shadowPut('manufacturer', updated.id, updated);
      },
      upsertDeviceType: t => {
        const cur = get().deviceTypes;
        const next = cur.some(x => x.id === t.id)
          ? cur.map(x => (x.id === t.id ? t : x))
          : [...cur, t];
        set({ deviceTypes: next });
        shadowPut('device_type', t.id, t);
      },
      deleteDeviceType: id => {
        set({ deviceTypes: get().deviceTypes.filter(t => t.id !== id) });
        shadowRemove('device_type', id);
      },
      upsertSparePart: p => {
        const cur = get().spareParts;
        const next = cur.some(x => x.articleNr === p.articleNr)
          ? cur.map(x => (x.articleNr === p.articleNr ? p : x))
          : [...cur, p];
        set({ spareParts: next });
        // spareParts key on articleNr (no `id` field)
        shadowPut('spare_part', p.articleNr, p);
      },
      deleteSparePart: articleNr => {
        set({
          spareParts: get().spareParts.filter(p => p.articleNr !== articleNr),
        });
        shadowRemove('spare_part', articleNr);
      },
      upsertServicePrice: p => {
        const cur = get().servicePrices;
        const next = cur.some(x => x.articleNr === p.articleNr)
          ? cur.map(x => (x.articleNr === p.articleNr ? p : x))
          : [...cur, p];
        set({ servicePrices: next });
        // servicePrices key on articleNr (no `id` field)
        shadowPut('service_price', p.articleNr, p);
      },
      deleteServicePrice: articleNr => {
        set({
          servicePrices: get().servicePrices.filter(
            p => p.articleNr !== articleNr
          ),
        });
        shadowRemove('service_price', articleNr);
      },
      upsertVisit: v => {
        const cur = get().visits;
        const next = cur.some(x => x.id === v.id)
          ? cur.map(x => (x.id === v.id ? v : x))
          : [...cur, v];
        set({ visits: next });
        shadowPut('visit', v.id, v);
      },
      deleteVisit: id => {
        set({ visits: get().visits.filter(v => v.id !== id) });
        shadowRemove('visit', id);
      },
      upsertWorker: w => {
        const cur = get().workers;
        const next = cur.some(x => x.id === w.id)
          ? cur.map(x => (x.id === w.id ? w : x))
          : [...cur, w];
        set({ workers: next });
        shadowPut('worker', w.id, w);
      },
      deleteWorker: id => {
        set({ workers: get().workers.filter(w => w.id !== id) });
        shadowRemove('worker', id);
      },
      startTour: () => set({ tourActive: true, tourStep: 0 }),
      nextTourStep: () => set({ tourStep: get().tourStep + 1 }),
      prevTourStep: () => set({ tourStep: Math.max(0, get().tourStep - 1) }),
      endTour: () => set({ tourActive: false, tourSeen: true, tourStep: 0 }),
      setTourStep: n => set({ tourStep: n }),
      markUpdatesSeen: () => set({ updatesSeen: true }),
      markUpdateItemVisited: key =>
        set(s =>
          s.visitedUpdates.includes(key)
            ? {}
            : { visitedUpdates: [...s.visitedUpdates, key] }
        ),
      setFocus: (selector, label = null) =>
        set({ focusSelector: selector, focusLabel: selector ? label : null }),
      replaceAll: data => {
        set(data as AppState);
        // Import path (ImportExport) replaces the customer list — shadow-put
        // each so the imported docs replicate to CouchDB, not just local state.
        if (data.customers) {
          data.customers.forEach(c => shadowPutCustomer(c));
        }
      },
    }),
    {
      // v4: domain data is NO LONGER persisted to localStorage. The backend
      // (CouchDB → PouchDB `ft-local` IndexedDB) is the sole source of truth;
      // the store hydrates from PouchDB on auth (see components/AuthGate.tsx).
      // Only UI preferences live here so they survive reload without caching
      // any backend-owned data locally.
      name: 'fire-track-app-v4',
      version: 4,
      partialize: state => ({
        tourSeen: state.tourSeen,
        updatesSeen: state.updatesSeen,
        visitedUpdates: state.visitedUpdates,
        lang: state.lang,
      }),
    }
  )
);

// Purge pre-v4 persisted blobs that cached backend-owned domain data in
// localStorage. v4 only persists UI prefs; these old keys are never read again.
if (typeof window !== 'undefined') {
  ['fire-track-app-v3', 'fire-track-app-v2', 'fire-track-app'].forEach(k => {
    try {
      window.localStorage.removeItem(k);
    } catch {
      // ignore — storage may be unavailable
    }
  });
}

// keep currentUser/activeTenantId in sync with auth state
onAuthChange(() => {
  const user = deriveUserFromJwt();
  useApp.setState({
    currentUser: user,
    activeTenantId: user?.tenantId ?? null,
  });
});

// Surface PouchDB write-through failures instead of silently dropping records.
// First failure toasts; the unsynced count drives any UI indicator; the queue
// retries automatically (timer / online / tab focus).
setSyncErrorHandler(() => {
  const { lang } = useApp.getState();
  useApp.getState().toast(t('sync.saveFailed', lang));
});
subscribeSyncQueue(() => {
  useApp.setState({ unsyncedCount: pendingCount() });
});
startSyncQueue();

// Surface server-side replication rejects (a doc denied by validate_doc_update
// on push — e.g. a role-forbidden write that slipped past the local guard, or a
// conflict). Without this the denial is swallowed and the record silently never
// reaches the server.
setReplicationErrorHandler(() => {
  const { lang } = useApp.getState();
  useApp.getState().toast(t('sync.rejected', lang));
});

// Drive the online flag from real connectivity instead of a manual toggle:
// browser online/offline events plus replication active / paused-with-error.
setOnlineHandler(v => useApp.getState().setOnline(v));
if (typeof window !== 'undefined') {
  const sync = (v: boolean) => useApp.getState().setOnline(v);
  sync(navigator.onLine);
  window.addEventListener('online', () => sync(true));
  window.addEventListener('offline', () => sync(false));
}
